/**
 * Shared result sorting utilities — used by both web and mobile.
 *
 * sortResults() is the main entry point. It reads the backend's
 * _queryClassification.type and routes to the correct strategy:
 *
 *   SPECIFIC_PLAYER_MARKET / PLAYER_PROP → line ladder (overs asc, unders desc)
 *   MATCHUP_QUERY                        → game_lines → derivatives → player_props
 *   SPECIFIC_TEAM_MARKET / TEAM_MARKETS  → requested market first, then sections
 *   BEST_BETS_ALL / LEAGUE_QUERY         → EV% descending
 */

// ─── Book name normalisation ──────────────────────────────────────────────────
// Strip spaces, underscores, punctuation and lowercase so "DraftKings",
// "draftkings" and "draft_kings" all map to the same token.
const _normBook = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/** Exported normalizer for use in components that need consistent book matching. */
export const normBookName = _normBook;

// Build a one-time lookup: norm(id) → norm(displayName) for books where they
// differ (e.g. id="si" → norm="si", display="SI Sportsbook" → norm="sisportsbook").
// This lets scoreMyBooksOverlap handle abbreviated IDs correctly.
import _sbJson from './sportsbooks.json';
const _BOOK_NORM_ALIASES = new Map();
(_sbJson || []).forEach(b => {
  const nId = _normBook(b.id || '');
  const nName = _normBook(b.name || '');
  if (nId && nName && nId !== nName) _BOOK_NORM_ALIASES.set(nId, nName);
});

// ─── Constants ────────────────────────────────────────────────────────────────

const SECTION_ORDER = {
  game_lines: 0,
  derivatives: 1,
  player_props: 2,
};

// Ordered list of [regex, priority] pairs. Lower priority = shown first.
const MARKET_PRIORITY_RULES = [
  [/moneyline|h2h|money.line|1x2/i, 1],
  [/point.spread|spread(?!\s+total)|run.line|puck.line|handicap|asian.handicap/i, 2],
  [/total.points|total.goals|total.runs|total.sets|total.games|^total$|over.under/i, 3],
  [/team.total/i, 4],
  [/draw.no.bet|double.chance/i, 5],
];

// ─── Market name normalization + alias groups ─────────────────────────────────

/**
 * Normalize a market name to a canonical token for comparison.
 * Strips spaces/underscores/plurals so "Money Line", "moneyline", "money_line" all match.
 */
const _WORD_TO_ORDINAL = {
  first: '1st', second: '2nd', third: '3rd', fourth: '4th',
  fifth: '5th', sixth: '6th', seventh: '7th', eighth: '8th',
  ninth: '9th', tenth: '10th',
};
const _ORDINAL_RE = new RegExp(`\\b(${Object.keys(_WORD_TO_ORDINAL).join('|')})\\b`, 'g');

function normalizeMarket(m) {
  return (m || '')
    .toLowerCase()
    .replace(_ORDINAL_RE, (_, w) => _WORD_TO_ORDINAL[w])
    .replace(/[_\s\-\/]+/g, '')
    .replace(/s$/, '');
}

/**
 * Auto-generated alias groups from ALL_OPTICODDS_MARKETS.json + keyword
 * variants. Regenerate with: npm run generate:frontend
 */
import _generatedAliases from './generatedMarketAliases.json';
const MARKET_ALIAS_GROUPS = _generatedAliases;

// Build a lookup: normalizedName → canonical group index
const _marketAliasMap = new Map();
MARKET_ALIAS_GROUPS.forEach((group, i) => {
  group.forEach(name => _marketAliasMap.set(normalizeMarket(name), i));
});

/**
 * Returns true if two market name strings are semantically equivalent.
 * Handles aliases, plurals, spacing/casing differences.
 */
function marketsMatch(a, b) {
  const nA = normalizeMarket(a);
  const nB = normalizeMarket(b);
  if (nA === nB) return true;
  // Check if they're in the same alias group
  const groupA = _marketAliasMap.get(nA);
  const groupB = _marketAliasMap.get(nB);
  if (groupA !== undefined && groupA === groupB) return true;
  // Substring fallback: one fully contains the other
  return nA.includes(nB) || nB.includes(nA);
}

// ─── Market section classification (shared across web + mobile) ──────────────

const _DERIVATIVE_RE = /\b(1st|2nd|3rd|4th|5th|6th|7th|8th|9th|first|second|third|fourth|fifth|sixth|seventh|eighth|ninth)\b.*(half|quarter|period|inning|set)|^(1st|2nd|3rd|4th)\s|alternate\s|alt\s|race\s*to|overtime|both\s*teams?\s*to\s*score|btts|halftime|half\s*time|double\s*chance|correct\s*score|highest\s*scoring|winning\s*margin|draw\s*no\s*bet|will\s*there\s*be/i;
const _PLAYER_RE = /^player[\s_]|pitcher|scorer|batsman|bowler|^first basket|^last basket|^method of/i;

/**
 * Classify a market name into 'game_lines', 'derivatives', or 'player_props'.
 * Works in both Node and browser environments (no fs/path dependency).
 */
function classifyMarketSection(marketName, isPlayerProp) {
  if (isPlayerProp) return 'player_props';
  const mkt = (marketName || '').toLowerCase();
  if (_PLAYER_RE.test(mkt)) return 'player_props';
  if (_DERIVATIVE_RE.test(mkt)) return 'derivatives';
  return 'game_lines';
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getMarketPriority(market) {
  const m = (market || '').toLowerCase();
  for (const [regex, priority] of MARKET_PRIORITY_RULES) {
    if (regex.test(m)) return priority;
  }
  return 10;
}

/** Extract the numeric line from a result object. */
function getLine(result) {
  const fromRaw = parseFloat(
    result.raw?.selection_line ??
    result.raw?.points ??
    result.raw?.line ??
    result.raw?.handicap ??
    NaN
  );
  if (!isNaN(fromRaw)) return fromRaw;
  return parseFloat(result.selection_line ?? result.points ?? result.line ?? result.handicap ?? NaN);
}

function isOverBet(result) {
  return /(^|\s|over)\s*\d/i.test(result.bet || '') ||
    (result.bet || result.raw?.name || result.raw?.selection || '').toLowerCase().includes('over');
}

function isUnderBet(result) {
  return (result.bet || result.raw?.name || result.raw?.selection || '').toLowerCase().includes('under');
}

/** MyBooks tiebreaker: return -1/0/1 indicating sort preference.
 *  Uses full bookOdds/allBooks overlap (via scoreMyBooksOverlap) so results
 *  available on any of the user's books — not just the best-priced book — rank higher.
 */
function myBooksCmp(a, b, myBooks) {
  if (!myBooks || myBooks.length === 0) return 0;
  const scoreA = scoreMyBooksOverlap(a, myBooks);
  const scoreB = scoreMyBooksOverlap(b, myBooks);
  if (Math.abs(scoreB - scoreA) > 0.001) return scoreB - scoreA;
  return 0;
}

/**
 * Score a result by what fraction of the user's myBooks appear in its
 * bookOdds / availableBooks / allBooks. Returns 0.0–1.0.
 * 1.0 = all myBooks available, 0.0 = none available.
 *
 * Handles abbreviated IDs (e.g. "si" stored in myBooks vs "SI Sportsbook"
 * as the bookOdds display key) via the _BOOK_NORM_ALIASES lookup.
 */
export function scoreMyBooksOverlap(item, myBooks) {
  if (!myBooks || myBooks.length === 0) return 0;
  // bookOdds may live at top level OR under .raw (SgpFlowScreen item shape)
  const bookOddsSource = item.bookOdds || item.raw?.bookOdds || {};
  const availableSource = item.availableBooks || item.raw?.availableBooks
    || item.allBooks || item.raw?.allBooks || [];
  const available = new Set([
    ...Object.keys(bookOddsSource),
    ...availableSource,
  ].map(_normBook));
  const hits = myBooks.filter(b => {
    const nId = _normBook(b);
    if (available.has(nId)) return true;
    const nDisplay = _BOOK_NORM_ALIASES.get(nId);
    return nDisplay ? available.has(nDisplay) : false;
  }).length;
  return hits / myBooks.length;
}

/**
 * Sort results using a two-tier My Books priority.
 *
 * Threshold: 1/myBooks.length — i.e. at least ONE of the user's books must be
 * available for a result to land in Tier A. This is the most intuitive rule:
 * "if I can place this bet on any of my books, show it first."
 *
 * Tier A (score ≥ threshold): bets placeable on at least one of the user's books.
 * Tier B (score <  threshold): bets with zero overlap — shown after.
 *
 * Within each tier, results are sorted by the provided `tierSortFn` (defaults to EV%).
 * Callers with a custom EV calculation (e.g. SgpFlowScreen) can pass their own comparator
 * so within-tier ordering stays consistent with the rest of their sort logic.
 *
 * @param {Array}    results     - Flat array of result objects
 * @param {Object}   options     - { myBooks: string[] }
 * @param {Function} [tierSortFn] - Optional comparator for within-tier sort (defaults to evCmp)
 */
function sortByMyBooksPriority(results, options = {}, tierSortFn = null) {
  const { myBooks = [] } = options;
  const sortFn = tierSortFn || ((a, b) => evCmp(a, b));
  if (!myBooks.length) return [...results].sort(sortFn);

  // Score every result once
  const scored = results.map(r => ({ r, s: scoreMyBooksOverlap(r, myBooks) }));

  // Fixed threshold: at least 1 of the user's books must be available (score > 0).
  // Using 1/myBooks.length as the floor: a score of exactly this means exactly 1 book matched.
  const threshold = 1 / myBooks.length;

  // Split into tiers; within each tier sort by the provided comparator
  const tierA = scored.filter(x => x.s >= threshold).map(x => x.r).sort(sortFn);
  const tierB = scored.filter(x => x.s <  threshold).map(x => x.r).sort(sortFn);

  return [...tierA, ...tierB];
}

function evCmp(a, b) {
  return parseFloat(b.evPercent || 0) - parseFloat(a.evPercent || 0);
}

// When My Books mode is active, sort by the best EV across the user's specific
// books (ev_percent_mybooks, backend-stamped by evCleanPerBook.js). Falls back
// to global evPercent when the stamp is absent (e.g. SGPs, legacy items).
function myBooksEvCmp(a, b) {
  const getRaw = (item) => item?.raw || item;
  const mbA = getRaw(a)?.ev_percent_mybooks;
  const mbB = getRaw(b)?.ev_percent_mybooks;
  if (Number.isFinite(mbA) && Number.isFinite(mbB)) return mbB - mbA;
  if (Number.isFinite(mbB)) return 1;   // b has My Books EV, a doesn't → b first
  if (Number.isFinite(mbA)) return -1;  // a has My Books EV, b doesn't → a first
  return evCmp(a, b);                   // neither stamped → fall back to global EV
}

// v1.20.8 — when a single book is the query focus, use that book's clean EV
// as the primary sort axis. Order: backend stamp `ev_percent_query_book` →
// the focused book's per-book `clean_ev_percent` → -Infinity (sink). The
// returned comparator is used by sortByEV when `preferredSportsbookKey` is
// set in options.
function _normBookFp(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/\s*\([^)]*\)\s*$/, '')
    .replace(/[^a-z0-9]+/g, '');
}

function makeFocusedEvCmp(preferredSportsbookKey) {
  const fp = _normBookFp(preferredSportsbookKey);
  if (!fp) return evCmp;
  const focusedEv = (item) => {
    if (!item) return -Infinity;
    const raw = item.raw || item;
    if (Number.isFinite(raw.ev_percent_query_book)) return raw.ev_percent_query_book;
    if (Number.isFinite(item.ev_percent_query_book)) return item.ev_percent_query_book;
    const bo = raw.bookOdds || item.bookOdds || {};
    for (const [k, v] of Object.entries(bo)) {
      if (_normBookFp(k) === fp) {
        if (v && Number.isFinite(v.clean_ev_percent)) return v.clean_ev_percent;
        break;
      }
    }
    return -Infinity;
  };
  return (a, b) => {
    const fA = focusedEv(a);
    const fB = focusedEv(b);
    if (fA !== fB) return fB - fA;
    return evCmp(a, b);
  };
}

// ─── Sorting Strategies ───────────────────────────────────────────────────────

/**
 * Player prop line ladder.
 *
 * - Overs are sorted ascending by line value (14.5, 17.5, 19.5, 22.5 …)
 * - Unders are sorted descending by line value (24.5, 22.5, 19.5 …)
 * - When user specified a direction, that side comes first.
 * - Ties within the same line are broken by EV%.
 * - After sorting, the single closest result to `entities.lineThreshold`
 *   (in the user's direction) is tagged with `_isClosestLine = true`.
 */
function sortPlayerPropResults(results, entities, options = {}) {
  const { myBooks = [], myBooksPriority = false } = options;
  const requestedDirection = (entities.lineDirection || '').toLowerCase();

  const sorted = [...results].sort((a, b) => {
    // 1. Direction match first (if user specified a direction)
    if (requestedDirection) {
      const aMatch = requestedDirection === 'over' ? isOverBet(a) : isUnderBet(a);
      const bMatch = requestedDirection === 'over' ? isOverBet(b) : isUnderBet(b);
      if (aMatch && !bMatch) return -1;
      if (!aMatch && bMatch) return 1;
    } else {
      // No direction: overs before unders
      const aIsOver = isOverBet(a);
      const bIsOver = isOverBet(b);
      if (aIsOver && !bIsOver) return -1;
      if (!aIsOver && bIsOver) return 1;
    }

    // 2. Within same direction, sort by line value
    const aLine = getLine(a);
    const bLine = getLine(b);
    if (!isNaN(aLine) && !isNaN(bLine) && Math.abs(aLine - bLine) > 0.01) {
      // Overs: ascending; Unders: descending
      const aIsOver = isOverBet(a);
      return aIsOver ? aLine - bLine : bLine - aLine;
    }

    // 3. My Books overlap — within the same line value, surface results that
    //    have more of the user's books available. Does not break the line ladder.
    if (myBooksPriority && myBooks.length > 0) {
      const scoreA = scoreMyBooksOverlap(a, myBooks);
      const scoreB = scoreMyBooksOverlap(b, myBooks);
      if (Math.abs(scoreB - scoreA) > 0.001) return scoreB - scoreA;
    }

    // 4. Tiebreak: EV%
    return evCmp(a, b);
  });

  // Tag closest line to requested threshold
  tagClosestLine(sorted, entities);
  return sorted;
}

/**
 * Matchup query sort: game_lines → derivatives → player_props.
 * Within game_lines: ML first, then Spread, then Total.
 */
function sortMatchupResults(results, entities, options = {}) {
  return [...results].sort((a, b) => {
    const aSec = SECTION_ORDER[a._resultSection] ?? 9;
    const bSec = SECTION_ORDER[b._resultSection] ?? 9;
    if (aSec !== bSec) return aSec - bSec;

    // Within game_lines: market priority
    if (a._resultSection === 'game_lines' || (!a._resultSection && !b._resultSection)) {
      const aPrio = getMarketPriority(a.market);
      const bPrio = getMarketPriority(b.market);
      if (aPrio !== bPrio) return aPrio - bPrio;
    }

    return evCmp(a, b);
  });
}

/**
 * Team market sort: requested market first, then section order, then EV%.
 * Uses alias-aware market matching so "team total" correctly matches
 * results with market="Team Totals" or "Team Total Points".
 */
function sortTeamResults(results, entities, options = {}) {
  const requestedMarket = entities.market || '';

  return [...results].sort((a, b) => {
    // Section order first
    const aSec = SECTION_ORDER[a._resultSection] ?? 9;
    const bSec = SECTION_ORDER[b._resultSection] ?? 9;
    if (aSec !== bSec) return aSec - bSec;

    // Requested market gets priority within its section (alias-aware)
    if (requestedMarket) {
      const aMatch = marketsMatch(a.market, requestedMarket);
      const bMatch = marketsMatch(b.market, requestedMarket);
      if (aMatch && !bMatch) return -1;
      if (!aMatch && bMatch) return 1;
    }

    // Market priority within section
    const aPrio = getMarketPriority(a.market);
    const bPrio = getMarketPriority(b.market);
    if (aPrio !== bPrio) return aPrio - bPrio;

    return evCmp(a, b);
  });
}

/**
 * Sort purely by EV%, with optional MyBooks preference.
 * When myBooksPriority is true and myBooks is non-empty, delegates to
 * sortByMyBooksPriority so the overlap-score tiers take effect first.
 */
function sortByEV(results, options = {}) {
  const {
    myBooks = [],
    defaultSortMethod = 'ev',
    myBooksPriority = false,
    preferredSportsbookKey = null,
  } = options;

  // v1.20.8 — book-focused queries (one book in scope) sort by that book's
  // clean EV directly, taking precedence over both myBooks priority and the
  // default EV axis. Matches the badge UnifiedBetCard renders for the row.
  if (preferredSportsbookKey) {
    const cmp = makeFocusedEvCmp(preferredSportsbookKey);
    return [...results].sort(cmp);
  }

  if (myBooksPriority && myBooks.length > 0) {
    return sortByMyBooksPriority(results, options, myBooksEvCmp);
  }
  return [...results].sort((a, b) => {
    if (defaultSortMethod === 'mybooks') {
      const mb = myBooksCmp(a, b, myBooks);
      if (mb !== 0) return mb;
    }
    return evCmp(a, b);
  });
}

// ─── Closest-Line Tagging ─────────────────────────────────────────────────────

/**
 * After sorting, find the result whose line is closest to `entities.lineThreshold`
 * (respecting direction). Tags it with `_isClosestLine = true`.
 *
 * Mutates the results array in place — call after sorting.
 */
function tagClosestLine(results, entities) {
  const requestedLine = entities.lineThreshold ?? null;
  if (requestedLine == null) return;

  let minDist = Infinity;
  let closestIdx = -1;

  results.forEach((r, i) => {
    if (!r.directionMatch) return; // wrong direction — ignore
    if (r.lineDistance == null) return;
    if (r.lineDistance < minDist) {
      minDist = r.lineDistance;
      closestIdx = i;
    }
  });

  results.forEach((r, i) => {
    r._isClosestLine = i === closestIdx && !r.isRequestedLine;
  });
}

// ─── Main Dispatcher ──────────────────────────────────────────────────────────

/**
 * Route to the correct sort strategy based on _queryClassification.type.
 *
 * @param {Array}  results        - Flat array of transformed result objects
 * @param {Object} classification - The backend's _queryClassification object
 * @param {Object} options        - { myBooks: string[], defaultSortMethod: string, myBooksPriority: boolean }
 * @returns {Array} New sorted array (does not mutate input except for _isClosestLine tags)
 */
export function sortResults(results, classification, options = {}) {
  if (!results || results.length === 0) return results;

  const type = classification?.type;
  const entities = classification?.entities || {};
  const hasRequestedLine = entities.lineThreshold != null || entities.spreadLine != null;

  // v1.20.8 — when the backend classification carries a single book (the
  // user typed it in the query), forward it as `preferredSportsbookKey` so
  // sortByEV uses the focused-book axis. Caller-supplied options always win
  // (e.g. UnifiedSearchScreen single-filter post-results state).
  const _opts = options.preferredSportsbookKey
    ? options
    : (classification?.sportsbook
      ? { ...options, preferredSportsbookKey: classification.sportsbook }
      : options);

  switch (type) {
    case 'SPECIFIC_PLAYER_MARKET':
      // Player + market, no specific line — show clean line ladder
      return sortPlayerPropResults(results, entities, _opts);

    case 'PLAYER_PROP':
      // Player + line threshold — line ladder with requested line highlighted
      return sortPlayerPropResults(results, entities, _opts);

    case 'MATCHUP_QUERY':
      // Default: sort by EV so best-value bets surface first regardless of market section.
      // Section-based grouping (game_lines → derivatives → props) is available via sortMatchupResults
      // but is no longer the default since users want best EV% first.
      if (hasRequestedLine) return sortPlayerPropResults(results, entities, _opts);
      return sortByEV(results, _opts);

    case 'SPECIFIC_TEAM_MARKET':
    case 'TEAM_MARKETS':
      // Default: EV sort. Backend also sets _backendSorted: true for these via globalEVSort,
      // but if the frontend falls through here (e.g. matchup sub-path without backendSorted),
      // still sort by EV so the result order is consistent.
      if (hasRequestedLine) return sortPlayerPropResults(results, entities, _opts);
      return sortByEV(results, _opts);

    case 'DERIVATIVE_MARKET':
      return sortByEV(results, _opts);

    case 'BEST_BETS_ALL':
    case 'LEAGUE_QUERY':
      if (hasRequestedLine) return sortPlayerPropResults(results, entities, _opts);
      return sortByEV(results, _opts);

    default:
      if (hasRequestedLine) return sortPlayerPropResults(results, entities, _opts);
      return sortByEV(results, _opts);
  }
}

// Named re-exports for testing and shared usage
export {
  sortByEV, sortByMyBooksPriority,
  sortPlayerPropResults, sortMatchupResults, sortTeamResults, tagClosestLine,
  classifyMarketSection, normalizeMarket, marketsMatch,
  MARKET_ALIAS_GROUPS, MARKET_PRIORITY_RULES, SECTION_ORDER,
};
